import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import type {
  SupportTicket,
  SupportTicketPriority,
  SupportTicketStatus,
  DebugVisibleEvent,
  SupportTicketReply,
  SupportInternalNote,
  SupportCannedReply,
  SupportMacro,
  SupportReporterRestriction,
  SupportDeliveryChannelResult,
} from '../../../types/supportTicket'
import {
  updateSupportTicket,
  addSupportTicketReply,
  getSupportTicketInternalNotes,
  listSupportCannedReplies,
  getSupportTicketById,
  listSupportMacros,
  runSupportMacro,
  mergeSupportTickets,
  updateSupportReporterRestriction,
  getPriorTicketsByReporter,
  getReporterRestriction,
  getRecentOwnerActionsForUser,
  getReporterAccountInfo,
  isTitleSimilar,
} from '../../../services/supportTicketsService'
import { calculateRemainingSlaMs, formatSlaRemaining } from '../../../utils/supportSla'
import { useSupportAccessReason } from '../shell/SupportAccessReasonDialog'
import { BLURRED_SCREENSHOT_PLACEHOLDER, redactTextBundle } from '../../../services/ownerRedaction'
import { OwnerFirmConfirm, OwnerHardConfirm } from '../kit/OwnerConfirm'
import { BreakGlassModal } from '../dialogs/BreakGlassModal'
import { navigateOwner } from '../ownerRouter'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'

import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded'

import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SnoozeRoundedIcon from '@mui/icons-material/SnoozeRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import FormatBoldRoundedIcon from '@mui/icons-material/FormatBoldRounded'
import FormatItalicRoundedIcon from '@mui/icons-material/FormatItalicRounded'
import CodeRoundedIcon from '@mui/icons-material/CodeRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import FlashOnRoundedIcon from '@mui/icons-material/FlashOnRounded'

interface OwnerSupportDetailProps {
  ticket: SupportTicket
  onTicketUpdated?: (updated: SupportTicket) => void
  onClose?: () => void
}

type TimelineFilter = 'all' | 'submission' | 'route' | 'error' | 'console' | 'network' | 'screenshot' | 'reply' | 'internal' | 'system'

type TimelineSubmissionPayload = {
  title?: string
  reporterName?: string
  reporterEmail?: string
  description?: string
  additionalMessage?: string
  screenshotCount?: number
  hasDiagnostics?: boolean
}

type TimelineEntry =
  | { id: string; timestamp: number; type: 'submission'; dateLabel: string; payload: TimelineSubmissionPayload }
  | { id: string; timestamp: number; type: 'route' | 'error' | 'console' | 'network' | 'screenshot' | 'system'; dateLabel: string; payload: DebugVisibleEvent }
  | { id: string; timestamp: number; type: 'reply'; dateLabel: string; payload: SupportTicketReply }
  | { id: string; timestamp: number; type: 'internal'; dateLabel: string; payload: SupportInternalNote }

const getTimestampMs = (val: unknown): number => {
  if (typeof val === 'number') return val
  if (val && typeof val === 'object' && 'toMillis' in val && typeof (val as { toMillis: unknown }).toMillis === 'function') {
    return (val as { toMillis: () => number }).toMillis()
  }
  if (typeof val === 'string') {
    const t = new Date(val).getTime()
    return Number.isNaN(t) ? 0 : t
  }
  return 0
}

type TicketWithContext = SupportTicket & {
  workspaceName?: string
  workspaceId?: string
  workspacePlan?: string
  debugSessionId?: string
}

// Minimal safe markdown renderer (no innerHTML, no dependencies)
function renderMinimalMarkdown(text: string): React.ReactNode {
  if (!text) return null
  const lines = text.split('\n')

  return lines.map((line, lIdx) => {
    // Process markdown tokens: code, bold, italic, link
    const tokens: React.ReactNode[] = []
    let cursor = 0

    // Match code `...`, bold **...**, italic *...*, link [text](url)
    const tokenRegex = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g
    let match: RegExpExecArray | null

    while ((match = tokenRegex.exec(line)) !== null) {
      if (match.index > cursor) {
        tokens.push(line.slice(cursor, match.index))
      }
      const raw = match[0]
      if (raw.startsWith('`') && raw.endsWith('`')) {
        tokens.push(
          <code key={`${lIdx}-${match.index}`} style={{ background: '#1e293b', padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace' }}>
            {raw.slice(1, -1)}
          </code>
        )
      } else if (raw.startsWith('**') && raw.endsWith('**')) {
        tokens.push(<strong key={`${lIdx}-${match.index}`}>{raw.slice(2, -2)}</strong>)
      } else if (raw.startsWith('*') && raw.endsWith('*')) {
        tokens.push(<em key={`${lIdx}-${match.index}`}>{raw.slice(1, -1)}</em>)
      } else if (raw.startsWith('[') && raw.includes('](')) {
        const parts = raw.slice(1, -1).split('](')
        tokens.push(
          <a
            key={`${lIdx}-${match.index}`}
            href={parts[1]}
            target="_blank"
            rel="noreferrer noopener"
            style={{ color: '#38bdf8', textDecoration: 'underline' }}
          >
            {parts[0]}
          </a>
        )
      }
      cursor = match.index + raw.length
    }

    if (cursor < line.length) {
      tokens.push(line.slice(cursor))
    }

    return (
      <span key={lIdx} style={{ display: 'block', minHeight: line.trim() ? undefined : '0.8em' }}>
        {tokens.length > 0 ? tokens : ' '}
      </span>
    )
  })
}

export const OwnerSupportDetail = ({
  ticket: initialTicket,
  onTicketUpdated,
  onClose,
}: OwnerSupportDetailProps) => {
  const { ensureAccessReason } = useSupportAccessReason()
  const [ticket, setTicket] = useState<SupportTicket>(initialTicket)

  const getTicketAccessReason = useCallback(async () => {
    return ensureAccessReason({
      key: `SUPPORT_TICKET:${ticket.id}`,
      defaultCode: 'SUPPORT_TICKET',
      defaultTicketId: ticket.id,
      onCancel: onClose,
    })
  }, [ensureAccessReason, ticket.id, onClose])
  const [copiedId, setCopiedId] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())

  // Internal notes state
  const [internalNotes, setInternalNotes] = useState<SupportInternalNote[]>([])
  // Internal delivery metadata keyed by replyId (from internal subcollection — not reporter-visible)
  const [replyMeta, setReplyMeta] = useState<Record<string, { authorId: string; authorRole: string; deliveryResults: Record<string, SupportDeliveryChannelResult> }>>({})

  // Canned replies state
  const [cannedReplies, setCannedReplies] = useState<SupportCannedReply[]>([])
  const [showCannedPicker, setShowCannedPicker] = useState(false)
  const [cannedSearch, setCannedSearch] = useState('')

  // Snooze dropdown state
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(false)
  const [customSnoozeDate, setCustomSnoozeDate] = useState('')

  // Tag editing state
  const [showAddTagInput, setShowAddTagInput] = useState(false)
  const [newTagText, setNewTagText] = useState('')

  // Break-glass modal state
  const [breakGlassOpen, setBreakGlassOpen] = useState(false)
  const [isRevealed, setIsRevealed] = useState(() => {
    try {
      return Boolean(sessionStorage.getItem('owner_breakglass_grant_id'))
    } catch {
      return false
    }
  })

  // 2-1 Macros state
  const [macros, setMacros] = useState<SupportMacro[]>([])
  const [showMacroPicker, setShowMacroPicker] = useState(false)
  const [macroSearch, setMacroSearch] = useState('')
  const [macroToConfirm, setMacroToConfirm] = useState<SupportMacro | null>(null)
  const [isRunningMacro, setIsRunningMacro] = useState(false)

  // 2-3 Merge state
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [mergeCandidates, setMergeCandidates] = useState<SupportTicket[]>([])
  const [selectedParentTicket, setSelectedParentTicket] = useState<SupportTicket | null>(null)
  const [showMergeHardConfirm, setShowMergeHardConfirm] = useState(false)
  const [isMerging, setIsMerging] = useState(false)

  // 2-5 Restrictions state
  const [showRestrictModal, setShowRestrictModal] = useState(false)
  const [restrictionDraft, setRestrictionDraft] = useState({
    blockNewTickets: false,
    blockReopens: false,
    slowMode: 0,
    autoTag: '',
    skipAutoP0: false,
    broadcastOptOut: false,
    reason: 'abusive_language',
    expiresAt: '',
  })
  const [showRestrictConfirm, setShowRestrictConfirm] = useState(false)
  const [isSavingRestriction, setIsSavingRestriction] = useState(false)

  // 2-6 Reporter-context sidebar state
  const [reporterInfo, setReporterInfo] = useState<{ email?: string; tenure?: string } | null>(null)
  const [activeRestriction, setActiveRestriction] = useState<SupportReporterRestriction | null>(null)
  const [priorTickets, setPriorTickets] = useState<SupportTicket[]>([])
  const [recentOwnerActions, setRecentOwnerActions] = useState<Array<{ id: string; kind: string; reason: string; createdAt: string; actorRole?: string }>>([])

  // Compose bar state
  const [composeMode, setComposeMode] = useState<'reply' | 'resolve' | 'internal_note'>('reply')
  const [composeText, setComposeText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isPreview, setIsPreview] = useState(false)

  // Firm confirm modal state for "Reply and resolve"
  const [showFirmConfirm, setShowFirmConfirm] = useState(false)

  // Timeline filter
  const [activeFilter, setActiveFilter] = useState<TimelineFilter>('all')

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Sync ticket when prop updates
  const [prevInitialTicket, setPrevInitialTicket] = useState(initialTicket)
  if (prevInitialTicket !== initialTicket) {
    setPrevInitialTicket(initialTicket)
    setTicket(initialTicket)
  }

  // Live timer for SLA pill
  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // Fetch internal notes from internal subcollection
  const refreshInternalNotes = useCallback(async () => {
    try {
      const reason = await getTicketAccessReason()
      if (!reason) return
      const res = await getSupportTicketInternalNotes(ticket.id, reason)
      if (res && res.notes) {
        setInternalNotes(res.notes as SupportInternalNote[])
      }
      if (res && res.replyMeta) {
        setReplyMeta(res.replyMeta as Record<string, { authorId: string; authorRole: string; deliveryResults: Record<string, SupportDeliveryChannelResult> }>)
      }
    } catch (err) {
      console.warn('Failed to load internal notes:', err)
    }
  }, [ticket.id, getTicketAccessReason])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const reason = await getTicketAccessReason()
      if (cancelled || !reason) return
      try {
        const res = await getSupportTicketInternalNotes(ticket.id, reason)
        if (cancelled || !res) return
        if (res.notes) setInternalNotes(res.notes as SupportInternalNote[])
        if (res.replyMeta) setReplyMeta(res.replyMeta as Record<string, { authorId: string; authorRole: string; deliveryResults: Record<string, SupportDeliveryChannelResult> }>)
      } catch (err) {
        console.warn('Failed to load internal notes:', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [ticket.id, getTicketAccessReason])

  // Fetch canned replies
  useEffect(() => {
    listSupportCannedReplies()
      .then((res) => {
        if (res && res.cannedReplies) {
          setCannedReplies(res.cannedReplies as SupportCannedReply[])
        }
      })
      .catch((err) => console.warn('Failed to load canned replies:', err))
  }, [])

  // Fetch macros
  useEffect(() => {
    listSupportMacros()
      .then((res) => {
        if (res && res.macros) setMacros(res.macros)
      })
      .catch((err) => console.warn('Failed to load macros:', err))
  }, [])

  // Load reporter sidebar context (tenure, restrictions, prior tickets, owner action log)
  useEffect(() => {
    const reporterUid = ticket.reporterUid || ticket.reporterId
    if (!reporterUid) return
    let active = true
    void (async () => {
      const reason = await getTicketAccessReason()
      if (!active || !reason) return
      getReporterAccountInfo(reporterUid, reason).then((info) => {
        if (active) setReporterInfo(info)
      })
      getReporterRestriction(reporterUid).then((restr) => {
        if (active) setActiveRestriction(restr)
      })
      getPriorTicketsByReporter(reporterUid, ticket.id, reason).then((pts) => {
        if (active) setPriorTickets(pts)
      })
      getRecentOwnerActionsForUser(reporterUid).then((acts) => {
        if (active) setRecentOwnerActions(acts)
      })
    })()
    return () => { active = false }
  }, [ticket.reporterUid, ticket.reporterId, ticket.id, getTicketAccessReason])


  // Copy Ticket ID
  const handleCopyId = useCallback(() => {
    navigator.clipboard.writeText(ticket.id)
    setCopiedId(true)
    setTimeout(() => setCopiedId(false), 1500)
  }, [ticket.id])

  // SLA Calculation
  const deadlineMs = getTimestampMs(ticket.slaDeadline)
  const pausedAtMs = ticket.slaPausedAt ? getTimestampMs(ticket.slaPausedAt) : null
  const remainingMs = calculateRemainingSlaMs(deadlineMs, pausedAtMs, nowMs)
  const { label: slaLabel, isBreached } = formatSlaRemaining(remainingMs)

  const refreshTicket = useCallback(async () => {
    const reason = await getTicketAccessReason()
    const updated = await getSupportTicketById(ticket.id, reason || undefined)
    if (updated) {
      setTicket(updated)
      onTicketUpdated?.(updated)
    }
    return updated
  }, [ticket.id, getTicketAccessReason, onTicketUpdated])

  // Status & Priority Mutations
  const handleStatusChange = async (nextStatus: SupportTicketStatus) => {
    try {
      await updateSupportTicket({ ticketId: ticket.id, status: nextStatus })
      await refreshTicket()
    } catch (err: unknown) {
      alert(`Failed to update status: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  const handlePriorityChange = async (nextPriority: SupportTicketPriority) => {
    try {
      await updateSupportTicket({ ticketId: ticket.id, priority: nextPriority })
      await refreshTicket()
    } catch (err: unknown) {
      alert(`Failed to update priority: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  // Snooze Actions
  const handleApplySnooze = async (date: Date) => {
    try {
      await updateSupportTicket({
        ticketId: ticket.id,
        status: 'snoozed',
        snoozedUntil: date.toISOString(),
      })
      setShowSnoozeMenu(false)
      await refreshTicket()
    } catch (err: unknown) {
      alert(`Failed to snooze ticket: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  const handleSnooze4Hours = () => {
    const d = new Date(Date.now() + 4 * 60 * 60 * 1000)
    handleApplySnooze(d)
  }

  const handleSnoozeTomorrow9am = () => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    d.setHours(9, 0, 0, 0)
    handleApplySnooze(d)
  }

  const handleSnoozeNextBusinessDay = () => {
    const d = new Date()
    const day = d.getDay() // 0 is Sun, 6 is Sat
    const daysToAdd = day === 5 ? 3 : day === 6 ? 2 : 1
    d.setDate(d.getDate() + daysToAdd)
    d.setHours(9, 0, 0, 0)
    handleApplySnooze(d)
  }

  // Tags Actions
  const handleRemoveTag = async (tagToRemove: string) => {
    const nextTags = (ticket.tags || []).filter((t) => t !== tagToRemove)
    try {
      await updateSupportTicket({ ticketId: ticket.id, tags: nextTags })
      const updated = { ...ticket, tags: nextTags }
      setTicket(updated)
      onTicketUpdated?.(updated)
    } catch (err: unknown) {
      alert(`Failed to remove tag: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  const handleAddTag = async () => {
    const t = newTagText.trim().toLowerCase()
    if (!t) return
    const current = ticket.tags || []
    if (current.includes(t)) {
      setShowAddTagInput(false)
      setNewTagText('')
      return
    }
    const nextTags = [...current, t]
    try {
      await updateSupportTicket({ ticketId: ticket.id, tags: nextTags })
      const updated = { ...ticket, tags: nextTags }
      setTicket(updated)
      onTicketUpdated?.(updated)
      setShowAddTagInput(false)
      setNewTagText('')
    } catch (err: unknown) {
      alert(`Failed to add tag: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  // Canned Reply Variable Interpolation
  const fillVariables = useCallback((template: string): string => {
    const ticketContext = ticket as TicketWithContext
    const reporterName = ticket.reporterName || ticket.reporterEmail?.split('@')[0] || 'there'
    const workspaceName = ticketContext.workspaceName || ticketContext.workspaceId || 'your workspace'
    const plan = ticketContext.workspacePlan || 'Growth'
    const subject = ticket.title || 'Support ticket'

    return template
      .replaceAll('{{reporter.displayName}}', reporterName)
      .replaceAll('{{reporter.name}}', reporterName)
      .replaceAll('{{reporter}}', reporterName)
      .replaceAll('{{reporter.email}}', ticket.reporterEmail || '')
      .replaceAll('{{workspace.name}}', workspaceName)
      .replaceAll('{{workspace}}', workspaceName)
      .replaceAll('{{workspace.plan}}', plan)
      .replaceAll('{{plan}}', plan)
      .replaceAll('{{ticket.subject}}', subject)
      .replaceAll('{{ticket.title}}', subject)
      .replaceAll('{{subject}}', subject)
  }, [ticket])

  const insertCannedReply = (canned: SupportCannedReply) => {
    const filled = fillVariables(canned.body)
    setComposeText((prev) => (prev.endsWith('/') ? prev.slice(0, -1) + filled : prev ? prev + '\n\n' + filled : filled))
    setShowCannedPicker(false)
    textareaRef.current?.focus()
  }

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setComposeText(val)
    if (val.endsWith('/')) {
      setShowCannedPicker(true)
      setCannedSearch('')
    } else if (showCannedPicker && !val.includes('/')) {
      setShowCannedPicker(false)
    }
  }

  // Text formatting helpers
  const insertFormatting = (prefix: string, suffix: string) => {
    const el = textareaRef.current
    if (!el) return
    const start = el.selectionStart
    const end = el.selectionEnd
    const selected = composeText.slice(start, end)
    const replacement = `${prefix}${selected || 'text'}${suffix}`
    const next = composeText.slice(0, start) + replacement + composeText.slice(end)
    setComposeText(next)
    setTimeout(() => {
      el.focus()
      el.setSelectionRange(start + prefix.length, start + prefix.length + (selected ? selected.length : 4))
    }, 50)
  }

  // Compose Submission
  const handleExecuteSend = async (resolutionReason?: string) => {
    const body = composeText.trim()
    if (!body) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      if (composeMode === 'internal_note') {
        await addSupportTicketReply({
          ticketId: ticket.id,
          body,
          mode: 'internal_note',
        })
        setComposeText('')
        await refreshInternalNotes()
      } else {
        const nextStatus = composeMode === 'resolve' ? 'resolved' : 'waiting-on-reporter'
        await addSupportTicketReply({
          ticketId: ticket.id,
          body,
          mode: 'reply',
          nextStatus,
          resolutionReason,
        })
        setComposeText('')
        await refreshTicket()
      }
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to dispatch message.')
    } finally {
      setIsSubmitting(false)
      setShowFirmConfirm(false)
    }
  }

  const handleSendClick = () => {
    if (composeMode === 'resolve') {
      setShowFirmConfirm(true)
    } else {
      void handleExecuteSend()
    }
  }

  // 2-1 Macro execution
  const handleSelectMacro = (macro: SupportMacro) => {
    setShowMacroPicker(false)
    const hasDestructive = macro.steps.some((s) => s.destructive)
    if (hasDestructive) {
      setMacroToConfirm(macro)
    } else {
      void executeMacro(macro)
    }
  }

  const executeMacro = async (macro: SupportMacro) => {
    setIsRunningMacro(true)
    try {
      const res = await runSupportMacro({ macroId: macro.id, ticketId: ticket.id })
      if (res && res.ok) {
        await refreshTicket()
        await refreshInternalNotes()
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to run macro')
    } finally {
      setIsRunningMacro(false)
      setMacroToConfirm(null)
    }
  }

  // 2-3 Merge handlers
  const handleOpenMerge = async () => {
    const reporterUid = ticket.reporterUid || ticket.reporterId
    if (!reporterUid) return
    setSelectedParentTicket(null)
    setShowMergeModal(true)
    const prior = await getPriorTicketsByReporter(reporterUid, ticket.id, { code: 'SUPPORT_TICKET', linkedId: ticket.id })
    setMergeCandidates(prior.filter((t) => !['merged', 'dismissed', 'resolved'].includes(t.status)))
  }

  const handleExecuteMerge = async () => {
    if (!selectedParentTicket) return
    setIsMerging(true)
    try {
      await mergeSupportTickets({
        parentId: selectedParentTicket.id,
        childId: ticket.id,
      })
      setShowMergeHardConfirm(false)
      setShowMergeModal(false)
      await refreshTicket()
      onClose?.()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to merge ticket')
    } finally {
      setIsMerging(false)
    }
  }

  // 2-5 Restrictions handlers
  const handleOpenRestrict = () => {
    if (activeRestriction) {
      setRestrictionDraft({
        blockNewTickets: Boolean(activeRestriction.blockNewTickets),
        blockReopens: Boolean(activeRestriction.blockReopens),
        slowMode: activeRestriction.slowMode?.maxPerDay || 0,
        autoTag: activeRestriction.autoTag?.tagKeys?.[0] || '',
        skipAutoP0: Boolean(activeRestriction.skipAutoP0),
        broadcastOptOut: Boolean(activeRestriction.broadcastOptOut),
        reason: activeRestriction.blockNewTickets?.reason || activeRestriction.blockReopens?.reason || 'abusive_language',
        expiresAt: typeof activeRestriction.blockNewTickets?.expiresAt === 'string' ? activeRestriction.blockNewTickets.expiresAt : '',
      })
    }
    setShowRestrictModal(true)
  }

  const handleSaveRestriction = async (confirmedReason: string) => {
    const reporterUid = ticket.reporterUid || ticket.reporterId
    if (!reporterUid) return
    setIsSavingRestriction(true)
    try {
      await updateSupportReporterRestriction({
        reporterUid,
        blockNewTickets: restrictionDraft.blockNewTickets,
        blockReopens: restrictionDraft.blockReopens,
        slowMode: restrictionDraft.slowMode,
        autoTag: restrictionDraft.autoTag || undefined,
        skipAutoP0: restrictionDraft.skipAutoP0,
        broadcastOptOut: restrictionDraft.broadcastOptOut,
        reason: confirmedReason || restrictionDraft.reason,
        expiresAt: restrictionDraft.expiresAt || undefined,
      })
      setShowRestrictConfirm(false)
      setShowRestrictModal(false)
      const reloaded = await getReporterRestriction(reporterUid)
      setActiveRestriction(reloaded)
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update restrictions')
    } finally {
      setIsSavingRestriction(false)
    }
  }

  // Compile Chronological Timeline Entries
  const timelineEntries = useMemo(() => {
    const entries: TimelineEntry[] = []

    const toDateKey = (ts: unknown): { ms: number; label: string } => {
      let ms = 0
      if (typeof ts === 'number') ms = ts
      else if (ts && typeof ts === 'object' && 'toMillis' in ts && typeof (ts as { toMillis: unknown }).toMillis === 'function') {
        ms = (ts as { toMillis: () => number }).toMillis()
      } else if (typeof ts === 'string') {
        const parsed = new Date(ts).getTime()
        ms = Number.isNaN(parsed) ? 0 : parsed
      }

      if (!ms) {
        return { ms: 0, label: '—' }
      }

      const d = new Date(ms)
      const now = new Date()
      const isToday = d.toDateString() === now.toDateString()
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      const isYesterday = yesterday.toDateString() === d.toDateString()

      const label = isToday
        ? 'Today'
        : isYesterday
        ? 'Yesterday'
        : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

      return { ms, label }
    }

    // 1. Initial Submission
    const createdInfo = toDateKey(ticket.createdAt)
    entries.push({
      id: `sub-${ticket.id}`,
      timestamp: createdInfo.ms,
      type: 'submission',
      dateLabel: createdInfo.label,
      payload: {
        title: ticket.title,
        description: ticket.description,
        additionalMessage: ticket.additionalMessage,
        reporterEmail: ticket.reporterEmail,
        reporterName: ticket.reporterName,
        screenshotCount: ticket.screenshotCount,
        hasDiagnostics: ticket.hasDiagnostics,
      },
    })

    // 2. Visible Events (Diagnostic, route, error, console, network, screenshots)
    if (Array.isArray(ticket.visibleEvents)) {
      ticket.visibleEvents.forEach((ev: DebugVisibleEvent) => {
        const evInfo = toDateKey(ev.at)
        let mappedType: 'route' | 'error' | 'console' | 'network' | 'screenshot' | 'system' = 'system'
        if (ev.type === 'route') mappedType = 'route'
        else if (ev.type === 'error') mappedType = 'error'
        else if (ev.type === 'console') mappedType = 'console'
        else if (ev.type === 'network') mappedType = 'network'
        else if (ev.type === 'screenshot') mappedType = 'screenshot'
        else if (ev.type === 'user_note') mappedType = 'system'

        entries.push({
          id: `ev-${ev.id}`,
          timestamp: evInfo.ms,
          type: mappedType,
          dateLabel: evInfo.label,
          payload: ev,
        })
      })
    }

    // 3. Owner Replies
    if (Array.isArray(ticket.replies)) {
      ticket.replies.forEach((rep: SupportTicketReply) => {
        const repInfo = toDateKey(rep.createdAt)
        entries.push({
          id: rep.id,
          timestamp: repInfo.ms,
          type: 'reply',
          dateLabel: repInfo.label,
          payload: rep,
        })
      })
    }

    // 4. Internal Notes (From internal subcollection)
    internalNotes.forEach((note) => {
      const noteInfo = toDateKey(note.createdAt)
      entries.push({
        id: note.id,
        timestamp: noteInfo.ms,
        type: 'internal',
        dateLabel: noteInfo.label,
        payload: note,
      })
    })

    // Sort chronologically
    entries.sort((a, b) => a.timestamp - b.timestamp)
    return entries
  }, [ticket, internalNotes])

  // Filtered timeline entries
  const filteredEntries = useMemo(() => {
    if (activeFilter === 'all') return timelineEntries
    return timelineEntries.filter((e) => e.type === activeFilter)
  }, [timelineEntries, activeFilter])

  // Filtered canned replies for popover
  const filteredCannedReplies = useMemo(() => {
    if (!cannedSearch.trim()) return cannedReplies
    const q = cannedSearch.toLowerCase().trim()
    return cannedReplies.filter(
      (c) => c.name.toLowerCase().includes(q) || c.body.toLowerCase().includes(q)
    )
  }, [cannedReplies, cannedSearch])

  return (
    <div className="ownerSupportDetailPane" data-testid="owner-support-detail-pane">
      {/* 1. Header */}
      <header className="ownerSupportDetailHeader">
        <div className="ownerSupportDetailTitleRow">
          <h2 className="ownerSupportDetailTitle">{ticket.title || 'Untitled Ticket'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              className="ownerTicketIdCopy"
              onClick={handleCopyId}
              title="Click to copy ticket ID"
            >
              #{ticket.id.slice(0, 10)}
              {copiedId ? (
                <CheckRoundedIcon style={{ fontSize: 13, color: '#10b981' }} />
              ) : (
                <ContentCopyRoundedIcon style={{ fontSize: 13 }} />
              )}
            </button>
            {onClose && (
              <button
                type="button"
                className="ownerSupportHeaderBtn"
                onClick={onClose}
                title="Close ticket detail (Esc)"
              >
                <CloseRoundedIcon style={{ fontSize: 14 }} />
              </button>
            )}
          </div>
        </div>

        <div className="ownerSupportDetailMeta">
          <span>{ticket.reporterEmail || 'anonymous'}</span>
          <span>•</span>
          <span>Workspace: {(ticket as TicketWithContext).workspaceName || (ticket as TicketWithContext).workspaceId || 'Default'}</span>
          <span>•</span>
          <span className={`ownerSlaPill ${isBreached ? 'ownerSlaPill--breached' : ''}`}>
            {ticket.status === 'waiting-on-reporter' ? `⏸ ${slaLabel}` : `⏰ ${slaLabel}`}
          </span>
          {(ticket.sessionId || (ticket as TicketWithContext).debugSessionId) && (
            <>
              <span>•</span>
              <button
                type="button"
                className="ownerSupportHeaderBtn"
                style={{ padding: '2px 8px', fontSize: 11, background: '#3b82f620', color: '#38bdf8', borderColor: '#38bdf840' }}
                onClick={() => navigateOwner({ view: 'logs', query: { debugSessionId: ticket.sessionId || (ticket as TicketWithContext).debugSessionId } })}
              >
                View trace ↗
              </button>
            </>
          )}
        </div>

        {/* Header Controls: Priority, Status, Snooze, Tags, Seams */}
        <div className="ownerSupportHeaderControls">
          {/* Priority Select */}
          <select
            className="ownerSupportSelect"
            value={ticket.priority}
            onChange={(e) => handlePriorityChange(e.target.value as SupportTicketPriority)}
            aria-label="Change priority"
          >
            <option value="P0">P0 (Blocker · 1h)</option>
            <option value="P1">P1 (Major · 4h)</option>
            <option value="P2">P2 (Normal · 24h)</option>
            <option value="P3">P3 (Minor · 5d)</option>
          </select>

          {/* Status Select */}
          <select
            className="ownerSupportSelect"
            value={ticket.status}
            onChange={(e) => handleStatusChange(e.target.value as SupportTicketStatus)}
            aria-label="Change status"
          >
            <option value="new">New</option>
            <option value="in-progress">In Progress</option>
            <option value="waiting-on-reporter">Waiting on Reporter</option>
            <option value="waiting-on-owner">Waiting on Owner</option>
            <option value="snoozed">Snoozed</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>

          {/* Snooze Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              className="ownerSupportHeaderBtn"
              onClick={() => setShowSnoozeMenu((prev) => !prev)}
            >
              <SnoozeRoundedIcon style={{ fontSize: 14 }} />
              Snooze ▾
            </button>
            {showSnoozeMenu && (
              <div className="ownerSupportSnoozePopover">
                <button
                  type="button"
                  className="ownerSupportSnoozeItem"
                  onClick={handleSnooze4Hours}
                >
                  <span>4 hours from now</span>
                  <small style={{ color: '#64748b' }}>+4h</small>
                </button>
                <button
                  type="button"
                  className="ownerSupportSnoozeItem"
                  onClick={handleSnoozeTomorrow9am}
                >
                  <span>Tomorrow 9:00 AM</span>
                  <small style={{ color: '#64748b' }}>9am</small>
                </button>
                <button
                  type="button"
                  className="ownerSupportSnoozeItem"
                  onClick={handleSnoozeNextBusinessDay}
                >
                  <span>Next business day (Mon)</span>
                  <small style={{ color: '#64748b' }}>9am</small>
                </button>
                <div style={{ padding: '6px 8px', borderTop: '1px solid #334155', marginTop: 4 }}>
                  <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4 }}>
                    Custom date/time:
                  </label>
                  <input
                    type="datetime-local"
                    className="ownerSupportSelect"
                    style={{ width: '100%' }}
                    value={customSnoozeDate}
                    onChange={(e) => setCustomSnoozeDate(e.target.value)}
                  />
                  {customSnoozeDate && (
                    <button
                      type="button"
                      className="ownerSupportHeaderBtn"
                      style={{ marginTop: 6, width: '100%', justifyContent: 'center' }}
                      onClick={() => handleApplySnooze(new Date(customSnoozeDate))}
                    >
                      Apply Snooze
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Tags Chips */}
          {(ticket.tags || []).map((tag) => (
            <span key={tag} className="ownerSupportTagBadge">
              <LocalOfferRoundedIcon style={{ fontSize: 11 }} />
              {tag}
              <button
                type="button"
                className="ownerSupportTagRemoveBtn"
                onClick={() => handleRemoveTag(tag)}
                title={`Remove tag ${tag}`}
              >
                ×
              </button>
            </span>
          ))}

          {showAddTagInput ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input
                type="text"
                className="ownerSupportSelect"
                placeholder="tag name"
                value={newTagText}
                autoFocus
                onChange={(e) => setNewTagText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddTag()
                  if (e.key === 'Escape') setShowAddTagInput(false)
                }}
                style={{ width: 90 }}
              />
              <button
                type="button"
                className="ownerSupportHeaderBtn"
                onClick={handleAddTag}
              >
                ✓
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="ownerSupportHeaderBtn"
              onClick={() => setShowAddTagInput(true)}
            >
              + Tag
            </button>
          )}

          <button
            type="button"
            className="ownerSupportHeaderBtn"
            onClick={handleOpenMerge}
            style={{ marginLeft: 'auto' }}
          >
            Merge
          </button>
          <button
            type="button"
            className="ownerSupportHeaderBtn"
            onClick={handleOpenRestrict}
          >
            Restrict
          </button>
        </div>
      </header>

      <div className="ownerSupportDetailBody">
        <div className="ownerSupportDetailMain">
          {/* 2. Message Thread (Chronological Timeline) */}
          <section className="ownerSupportThreadArea" aria-label="Ticket conversation timeline">
        {/* Type Filter Chips */}
        <div className="ownerSupportTimelineFilters">
          <span style={{ fontSize: 11, color: '#64748b', marginRight: 4 }}>Filter:</span>
          {[
            { id: 'all', label: 'All' },
            { id: 'submission', label: 'Submission' },
            { id: 'reply', label: 'Replies' },
            { id: 'internal', label: 'Notes 🔒' },
            { id: 'route', label: 'Routes' },
            { id: 'error', label: 'Errors' },
            { id: 'console', label: 'Console' },
            { id: 'network', label: 'Network' },
            { id: 'screenshot', label: 'Screenshots' },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              className={`ownerSupportTimelineFilterBtn ${
                activeFilter === f.id ? 'ownerSupportTimelineFilterBtn--active' : ''
              }`}
              onClick={() => setActiveFilter(f.id as TimelineFilter)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Timeline Entries Grouped by Day */}
        {filteredEntries.map((entry, idx) => {
          const prevEntry = filteredEntries[idx - 1]
          const showDateDivider = !prevEntry || prevEntry.dateLabel !== entry.dateLabel

          return (
            <div key={entry.id}>
              {showDateDivider && (
                <div className="ownerSupportDayDivider">
                  <hr />
                  <span>{entry.dateLabel}</span>
                  <hr />
                </div>
              )}

              {/* 2a. Initial User Submission */}
              {entry.type === 'submission' && (
                <article className="ownerSupportUserCard" data-testid="user-submission-card">
                  <div className="ownerSupportCardHeader">
                    <div className="ownerSupportCardAuthor">
                      <div className="ownerSupportAvatar">
                        {(entry.payload.reporterName?.[0] || entry.payload.reporterEmail?.[0] || 'U').toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {entry.payload.reporterName || entry.payload.reporterEmail || 'Reporter'}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          Reported · {new Date(entry.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ownerSupportCardBody">
                    {entry.payload.description || 'No description provided.'}
                    {entry.payload.additionalMessage && (
                      <div style={{ marginTop: 8, fontStyle: 'italic', color: '#94a3b8' }}>
                        Note: {entry.payload.additionalMessage}
                      </div>
                    )}
                  </div>

                  {/* Screenshot with Decoy Placeholder & Break-glass */}
                  {(entry.payload.screenshotCount ?? 0) > 0 && (
                    <div className="ownerSupportDecoyScreenshot">
                      <img
                        src={isRevealed && ticket.bundlePath ? ticket.bundlePath : BLURRED_SCREENSHOT_PLACEHOLDER}
                        alt="Screenshot preview"
                        className="ownerSupportDecoyImg"
                      />
                      {!isRevealed && (
                        <div className="ownerSupportDecoyOverlay">
                          <LockRoundedIcon style={{ color: '#fbbf24', fontSize: 24 }} />
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#f1f5f9' }}>
                            Content hidden by default — Break-glass required
                          </div>
                          <button
                            type="button"
                            className="ownerSupportHeaderBtn"
                            style={{ background: '#3b82f6', color: '#ffffff', borderColor: '#2563eb' }}
                            onClick={() => setBreakGlassOpen(true)}
                          >
                            <LockOpenRoundedIcon style={{ fontSize: 14 }} />
                            Break-glass reveal
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </article>
              )}

              {/* 2b. Owner Replies */}
              {entry.type === 'reply' && (
                <article className="ownerSupportReplyCard" data-testid="owner-reply-card">
                  <div className="ownerSupportReplyCardHeader">
                    <div className="ownerSupportCardAuthor">
                      <div className="ownerSupportAvatar" style={{ background: '#10b98130', color: '#10b981' }}>
                        S
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>
                          {entry.payload.authorLabel || 'Timelining Support'}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {new Date(entry.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>

                    {/* Delivery Status Badges — sourced from internal subcollection, not reporter-readable doc */}
                    {entry.payload.id && replyMeta[entry.payload.id]?.deliveryResults && (
                      <div className="ownerSupportDeliveryBadges">
                        {Object.entries(replyMeta[entry.payload.id].deliveryResults).map(([ch, res]) => (
                          <span
                            key={ch}
                            className={`ownerSupportDeliveryBadge ownerSupportDeliveryBadge--${res?.status || 'DELIVERED'}`}
                            title={res?.reason || res?.error || `Channel ${ch}: ${res?.status}`}
                          >
                            {ch === 'IN_APP' ? 'In-App' : ch}: {res?.status}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="ownerSupportCardBody">
                    {renderMinimalMarkdown(entry.payload.body)}
                  </div>
                </article>
              )}

              {/* 2c. Owner Internal Notes - VISUALLY UNMISTAKABLE */}
              {entry.type === 'internal' && (
                <article className="ownerSupportInternalNoteCard" data-testid="internal-note-card">
                  <div className="ownerSupportInternalNoteHeader">
                    <div className="ownerSupportInternalNoteBanner">
                      <LockRoundedIcon style={{ fontSize: 15 }} />
                      INTERNAL NOTE — NOT VISIBLE TO REPORTER
                    </div>
                    <div style={{ fontSize: 11, color: '#fbbf24' }}>
                      {entry.payload.authorName} • {new Date(entry.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                  <div className="ownerSupportCardBody" style={{ color: '#fef3c7' }}>
                    {renderMinimalMarkdown(entry.payload.body)}
                  </div>
                </article>
              )}

              {/* 2d. Route, Errors, Console, Network Events */}
              {entry.type !== 'submission' && entry.type !== 'reply' && entry.type !== 'internal' && (
                <div
                  className={`ownerSupportTimelineRow ${
                    entry.type === 'error' ? 'ownerSupportTimelineRow--error' : ''
                  }`}
                >
                  <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#64748b', minWidth: 50 }}>
                    {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      padding: '1px 5px',
                      borderRadius: 4,
                      background: entry.type === 'error' ? '#ef444430' : '#1e293b',
                      color: entry.type === 'error' ? '#ef4444' : '#94a3b8',
                    }}
                  >
                    {entry.type}
                  </span>
                  <div className="ownerSupportTimelineSummary" style={{ flex: 1 }}>
                    {entry.payload.summary || entry.payload.path || JSON.stringify(entry.payload)}
                    {entry.payload.details?.excerpt && (
                      <div className="ownerSupportScrubbedLogs">
                        {redactTextBundle(String(entry.payload.details.excerpt))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {filteredEntries.length === 0 && (
          <div style={{ textAlign: 'center', color: '#64748b', padding: '30px 0', fontSize: 13 }}>
            No timeline items match the selected filter.
          </div>
        )}
      </section>

      {/* 3. Compose Bar (Pinned Bottom) */}
      <footer
        className={`ownerSupportComposeBar ${
          composeMode === 'internal_note' ? 'ownerSupportComposeBar--internalNote' : ''
        }`}
      >
        {/* Mode Selector Tabs */}
        <div className="ownerSupportModeTabs">
          <button
            type="button"
            className={`ownerSupportModeTab ${
              composeMode === 'reply' ? 'ownerSupportModeTab--activeReply' : ''
            }`}
            onClick={() => setComposeMode('reply')}
          >
            Reply (Wait on reporter)
          </button>
          <button
            type="button"
            className={`ownerSupportModeTab ${
              composeMode === 'resolve' ? 'ownerSupportModeTab--activeResolve' : ''
            }`}
            onClick={() => setComposeMode('resolve')}
          >
            Reply and resolve
          </button>
          <button
            type="button"
            className={`ownerSupportModeTab ${
              composeMode === 'internal_note' ? 'ownerSupportModeTab--activeInternal' : ''
            }`}
            onClick={() => setComposeMode('internal_note')}
          >
            <LockRoundedIcon style={{ fontSize: 12, marginRight: 4 }} />
            Internal note
          </button>
        </div>

        {/* Formatting Actions Row */}
        <div className="ownerSupportComposeActionsRow">
          <div className="ownerSupportFormatBtns">
            <button
              type="button"
              className="ownerSupportFormatBtn"
              onClick={() => insertFormatting('**', '**')}
              title="Bold (**text**)"
            >
              <FormatBoldRoundedIcon style={{ fontSize: 14 }} />
            </button>
            <button
              type="button"
              className="ownerSupportFormatBtn"
              onClick={() => insertFormatting('*', '*')}
              title="Italic (*text*)"
            >
              <FormatItalicRoundedIcon style={{ fontSize: 14 }} />
            </button>
            <button
              type="button"
              className="ownerSupportFormatBtn"
              onClick={() => insertFormatting('`', '`')}
              title="Inline code (`code`)"
            >
              <CodeRoundedIcon style={{ fontSize: 14 }} />
            </button>
            <button
              type="button"
              className="ownerSupportFormatBtn"
              onClick={() => insertFormatting('[', '](https://)')}
              title="Link ([label](url))"
            >
              <LinkRoundedIcon style={{ fontSize: 14 }} />
            </button>
            <button
              type="button"
              className="ownerSupportFormatBtn"
              onClick={() => setShowCannedPicker((prev) => !prev)}
              title="Insert Canned Reply (Type /)"
              style={{ color: '#38bdf8' }}
            >
              <FlashOnRoundedIcon style={{ fontSize: 14, marginRight: 2 }} />
              / Canned
            </button>
            <button
              type="button"
              className="ownerSupportFormatBtn"
              onClick={() => setShowMacroPicker((prev) => !prev)}
              title="Run Macro (⌘/)"
              style={{ color: '#c084fc' }}
            >
              <BoltRoundedIcon style={{ fontSize: 14, marginRight: 2 }} />
              ⌘/ Macro
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              className="ownerSupportFormatBtn"
              onClick={() => setIsPreview((prev) => !prev)}
            >
              {isPreview ? 'Edit' : 'Preview'}
            </button>
          </div>
        </div>

        {/* Canned Reply Picker Popover */}
        {showCannedPicker && (
          <div className="ownerSupportCannedDropdown">
            <div style={{ padding: '4px 6px', borderBottom: '1px solid #334155', marginBottom: 4 }}>
              <input
                type="text"
                className="ownerSupportSelect"
                placeholder="Search canned replies..."
                value={cannedSearch}
                autoFocus
                onChange={(e) => setCannedSearch(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            {filteredCannedReplies.map((canned) => (
              <div
                key={canned.id}
                className="ownerSupportCannedItem"
                onClick={() => insertCannedReply(canned)}
              >
                <div className="ownerSupportCannedName">{canned.name}</div>
                <div className="ownerSupportCannedPreview">{canned.body.slice(0, 70)}...</div>
              </div>
            ))}
            {filteredCannedReplies.length === 0 && (
              <div style={{ fontSize: 11, color: '#64748b', padding: '6px 8px' }}>
                No canned replies found.
              </div>
            )}
          </div>
        )}

        {/* Macro Picker Popover */}
        {showMacroPicker && (
          <div className="ownerSupportCannedDropdown">
            <div style={{ padding: '4px 6px', borderBottom: '1px solid #334155', marginBottom: 4 }}>
              <input
                type="text"
                className="ownerSupportSelect"
                placeholder="Search macros (⌘/)..."
                value={macroSearch}
                autoFocus
                onChange={(e) => setMacroSearch(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            {macros
              .filter((m) => !macroSearch.trim() || m.name.toLowerCase().includes(macroSearch.toLowerCase()))
              .map((macro) => (
                <div
                  key={macro.id}
                  className="ownerSupportCannedItem"
                  onClick={() => handleSelectMacro(macro)}
                >
                  <div className="ownerSupportCannedName" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{macro.name}</span>
                    {macro.steps.some((s) => s.destructive) && (
                      <span style={{ fontSize: 10, color: '#ef4444', background: '#ef444420', padding: '1px 4px', borderRadius: 3 }}>
                        Destructive
                      </span>
                    )}
                  </div>
                  <div className="ownerSupportCannedPreview">
                    {macro.description || `${macro.steps.length} steps: ${macro.steps.map((s) => s.kind).join(', ')}`}
                  </div>
                </div>
              ))}
            {macros.length === 0 && (
              <div style={{ fontSize: 11, color: '#64748b', padding: '6px 8px' }}>
                No macros available.
              </div>
            )}
          </div>
        )}

        {/* Compose Input or Preview */}
        {isPreview ? (
          <div
            className="ownerSupportTextarea"
            style={{ minHeight: 80, overflowY: 'auto', background: '#090d16' }}
          >
            {renderMinimalMarkdown(composeText || '*(Nothing to preview)*')}
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            id="owner-support-compose-textarea"
            className="ownerSupportTextarea"
            placeholder={
              composeMode === 'internal_note'
                ? 'Write an internal note (only visible to owner console team)...'
                : 'Write a reply to the customer (type / for canned replies)...'
            }
            value={composeText}
            onChange={handleTextareaChange}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === '/') {
                e.preventDefault()
                setShowMacroPicker((prev) => !prev)
              }
            }}
          />
        )}

        {submitError && (
          <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{submitError}</div>
        )}

        {/* Footer with Send Button */}
        <div className="ownerSupportComposeFooter">
          <span style={{ fontSize: 11, color: '#64748b' }}>
            Shortcuts: <strong>r</strong> reply • <strong>n</strong> internal note • <strong>/</strong> canned • <strong>esc</strong> close
          </span>
          <button
            type="button"
            className="ownerSupportHeaderBtn"
            style={{
              background:
                composeMode === 'internal_note'
                  ? '#d97706'
                  : composeMode === 'resolve'
                  ? '#10b981'
                  : '#2563eb',
              color: '#ffffff',
              borderColor: 'transparent',
              fontWeight: 600,
              padding: '6px 14px',
            }}
            disabled={isSubmitting || !composeText.trim()}
            onClick={handleSendClick}
          >
            {isSubmitting ? (
              'Sending…'
            ) : composeMode === 'internal_note' ? (
              <>
                <LockRoundedIcon style={{ fontSize: 13, marginRight: 4 }} />
                Save Internal Note
              </>
            ) : composeMode === 'resolve' ? (
              <>
                <CheckCircleRoundedIcon style={{ fontSize: 14, marginRight: 4 }} />
                Reply & Resolve
              </>
            ) : (
              <>
                <SendRoundedIcon style={{ fontSize: 13, marginRight: 4 }} />
                Send Reply
              </>
            )}
          </button>
        </div>
      </footer>
    </div>

    {/* 4-Section Reporter Context Sidebar (Omit other 5 per 09 § 17c) */}
    <aside className="ownerSupportDetailSidebar" aria-label="Reporter context sidebar">
      {/* 1. Reporter Identity */}
      <section className="ownerSupportSidebarSection">
        <div className="ownerSupportSidebarTitle">
          <span>Reporter Identity</span>
        </div>
        <div className="ownerSupportSidebarCard">
          <strong style={{ color: '#f1f5f9', wordBreak: 'break-all' }}>
            {ticket.reporterEmail || reporterInfo?.email || 'Anonymous'}
          </strong>
          <span style={{ color: '#94a3b8', fontSize: 11 }}>
            Tenure: {reporterInfo?.tenure || 'Recent User'}
          </span>
          <span style={{ color: '#94a3b8', fontSize: 11 }}>
            Restrictions: {activeRestriction?.blockNewTickets ? '⛔ Blocked' : activeRestriction ? '⚠️ Restricted' : 'None'}
          </span>
          {/* [SEAM: Phase 1b People Hub] jump to people hub */}
          {/* [SEAM: Phase 1c Workspaces Hub] their workspaces */}
          {/* [SEAM: Phase 1b] their credits */}
        </div>
      </section>

      {/* 2. Restrictions */}
      <section className="ownerSupportSidebarSection">
        <div className="ownerSupportSidebarTitle">
          <span>Restrictions</span>
          <button
            type="button"
            className="ownerSupportFormatBtn"
            onClick={handleOpenRestrict}
            style={{ fontSize: 11, padding: '1px 6px' }}
          >
            Edit
          </button>
        </div>
        <div className="ownerSupportSidebarCard">
          {activeRestriction ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {activeRestriction.blockNewTickets && (
                <span style={{ color: '#ef4444' }}>• Block new tickets</span>
              )}
              {activeRestriction.blockReopens && (
                <span style={{ color: '#ef4444' }}>• Block reopens</span>
              )}
              {Boolean(activeRestriction.slowMode?.maxPerDay) && (
                <span style={{ color: '#f59e0b' }}>• Slow mode: {activeRestriction.slowMode?.maxPerDay} / day</span>
              )}
              {activeRestriction.skipAutoP0 && (
                <span style={{ color: '#f59e0b' }}>• Skip auto-P0 escalation</span>
              )}
              {activeRestriction.broadcastOptOut && (
                <span style={{ color: '#94a3b8' }}>• Broadcast opt-out</span>
              )}
              <span style={{ color: '#64748b', fontSize: 10, marginTop: 4 }}>
                Reason: {activeRestriction.blockNewTickets?.reason || activeRestriction.blockReopens?.reason || 'abusive_language'}
              </span>
            </div>
          ) : (
            <span style={{ color: '#64748b' }}>No active restrictions</span>
          )}
        </div>
      </section>

      {/* 3. Prior Tickets (last 10 + duplicate hint) */}
      <section className="ownerSupportSidebarSection">
        <div className="ownerSupportSidebarTitle">
          <span>Prior Tickets ({Array.isArray(priorTickets) ? priorTickets.length : 0})</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(Array.isArray(priorTickets) ? priorTickets : []).map((pt) => {
            const isDup = isTitleSimilar(ticket.title, pt.title)
            return (
              <div
                key={pt.id}
                className="ownerSupportPriorTicketItem"
                onClick={() => navigateOwner({ view: 'inbox', query: { selectedId: pt.id } })}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <strong style={{ fontSize: 12, color: '#f1f5f9' }}>
                    #{pt.id.slice(0, 8)}
                  </strong>
                  <span className={`ownerSupportChip ownerSupportChip--${pt.status}`}>
                    {pt.status}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span>{pt.title.slice(0, 24)}...</span>
                  {isDup && <span className="ownerSupportDuplicateBadge">Duplicate hint</span>}
                </div>
              </div>
            )
          })}
          {(!Array.isArray(priorTickets) || priorTickets.length === 0) && (
            <div style={{ color: '#64748b', fontSize: 11 }}>No prior tickets</div>
          )}
          {/* [SEAM: Phase 1b] recent activity */}
          {/* [SEAM: Phase 1b] feature flags in effect */}
          {/* [SEAM: Phase 1b] cohort memberships */}
        </div>
      </section>

      {/* 4. Recent Owner Actions */}
      <section className="ownerSupportSidebarSection">
        <div className="ownerSupportSidebarTitle">
          <span>Recent Owner Actions</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(Array.isArray(recentOwnerActions) ? recentOwnerActions : []).map((act) => (
            <div key={act.id} className="ownerSupportActionRow">
              <div style={{ fontWeight: 600, color: '#e2e8f0' }}>{act.kind}</div>
              <div style={{ color: '#94a3b8', fontSize: 10 }}>{act.reason || 'Action executed'}</div>
            </div>
          ))}
          {(!Array.isArray(recentOwnerActions) || recentOwnerActions.length === 0) && (
            <div style={{ color: '#64748b', fontSize: 11 }}>No recent actions logged</div>
          )}
        </div>
      </section>
    </aside>
  </div>

      {/* Firm Confirm Modal for "Reply and resolve" */}
      <OwnerFirmConfirm
        isOpen={showFirmConfirm}
        title="Resolve Support Ticket"
        description={
          <span>
            Are you sure you want to send this reply and mark ticket <strong>#{ticket.id.slice(0, 10)}</strong> as resolved?
            This will notify the customer via In-App / Push.
          </span>
        }
        confirmLabel="Resolve and Send"
        busy={isSubmitting}
        onClose={() => setShowFirmConfirm(false)}
        onConfirm={(reason) => handleExecuteSend(reason)}
      />

      {/* Break-glass modal for screenshot reveal */}
      <BreakGlassModal
        open={breakGlassOpen}
        subject={ticket.id}
        defaultReason="SUPPORT_TICKET"
        defaultLinkedId={ticket.id}
        onClose={() => setBreakGlassOpen(false)}
        onSuccess={() => {
          setIsRevealed(true)
        }}
      />

      {/* Merge Selection Modal */}
      {showMergeModal && (
        <div className="ownerModalOverlay" onClick={() => setShowMergeModal(false)} role="dialog">
          <div className="ownerModalDialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="ownerModalHeader">
              <span className="ownerModalTitle">Merge Ticket #{ticket.id.slice(0, 8)}</span>
              <button type="button" className="ownerSupportHeaderBtn" onClick={() => setShowMergeModal(false)}>✕</button>
            </div>
            <div className="ownerModalBody" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>
                Select an open ticket from <strong>{ticket.reporterEmail}</strong> to merge into.
                This ticket will become a child of the selected parent, its SLA will stop, and the reporter will see only the parent ticket.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto' }}>
                {mergeCandidates.map((candidate) => (
                  <div
                    key={candidate.id}
                    onClick={() => setSelectedParentTicket(candidate)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 6,
                      border: selectedParentTicket?.id === candidate.id ? '1px solid #3b82f6' : '1px solid #334155',
                      background: selectedParentTicket?.id === candidate.id ? '#1e3a8a30' : '#141824',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: 13, color: '#f1f5f9' }}>{candidate.title || 'Untitled'}</strong>
                      <span className={`ownerSupportChip ownerSupportChip--${candidate.status}`}>{candidate.status}</span>
                    </div>
                    <span style={{ fontSize: 11, color: '#64748b' }}>#{candidate.id.slice(0, 10)}</span>
                  </div>
                ))}
                {mergeCandidates.length === 0 && (
                  <div style={{ fontSize: 12, color: '#64748b', padding: '12px', textAlign: 'center' }}>
                    No other open tickets found for this reporter.
                  </div>
                )}
              </div>
            </div>
            <div className="ownerModalActions">
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--default"
                onClick={() => setShowMergeModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--primary"
                disabled={!selectedParentTicket || isMerging}
                onClick={() => setShowMergeHardConfirm(true)}
              >
                Continue to Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hard Confirm Modal for Merge (echoing both ticket subjects per 04/09) */}
      <OwnerHardConfirm
        isOpen={showMergeHardConfirm}
        title="Confirm Merge"
        targetHumanName={selectedParentTicket?.title || ''}
        description={
          <span>
            Merge <strong>"{ticket.title}"</strong> into <strong>"{selectedParentTicket?.title}"</strong>?
            This will stop the child ticket SLA, inline replies, and hide the child from the reporter.
            Type the parent ticket subject to confirm.
          </span>
        }
        busy={isMerging}
        onClose={() => setShowMergeHardConfirm(false)}
        onConfirm={handleExecuteMerge}
      />

      {/* Restrictions Modal */}
      {showRestrictModal && (
        <div className="ownerModalOverlay" onClick={() => setShowRestrictModal(false)} role="dialog">
          <div className="ownerModalDialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="ownerModalHeader">
              <span className="ownerModalTitle">Reporter Restrictions</span>
              <button type="button" className="ownerSupportHeaderBtn" onClick={() => setShowRestrictModal(false)}>✕</button>
            </div>
            <div className="ownerModalBody" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 13, color: '#94a3b8' }}>
                Account: <strong>{ticket.reporterEmail}</strong>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={restrictionDraft.blockNewTickets}
                  onChange={(e) => setRestrictionDraft((d) => ({ ...d, blockNewTickets: e.target.checked }))}
                />
                Block new tickets
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={restrictionDraft.blockReopens}
                  onChange={(e) => setRestrictionDraft((d) => ({ ...d, blockReopens: e.target.checked }))}
                />
                Block reopening resolved tickets
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={restrictionDraft.skipAutoP0}
                  onChange={(e) => setRestrictionDraft((d) => ({ ...d, skipAutoP0: e.target.checked }))}
                />
                Skip auto-P0 escalation
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={restrictionDraft.broadcastOptOut}
                  onChange={(e) => setRestrictionDraft((d) => ({ ...d, broadcastOptOut: e.target.checked }))}
                />
                Opt out of non-critical broadcasts
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, color: '#94a3b8' }}>Slow mode (max tickets per day, 0 = none):</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="ownerSupportSelect"
                  value={restrictionDraft.slowMode}
                  onChange={(e) => setRestrictionDraft((d) => ({ ...d, slowMode: Number(e.target.value) || 0 }))}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, color: '#94a3b8' }}>Auto-tag incoming tickets:</label>
                <input
                  type="text"
                  placeholder="e.g. frequent-reporter"
                  className="ownerSupportSelect"
                  value={restrictionDraft.autoTag}
                  onChange={(e) => setRestrictionDraft((d) => ({ ...d, autoTag: e.target.value }))}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, color: '#94a3b8' }}>Reason (Required ENUM):</label>
                <select
                  className="ownerSupportSelect"
                  value={restrictionDraft.reason}
                  onChange={(e) => setRestrictionDraft((d) => ({ ...d, reason: e.target.value }))}
                >
                  <option value="abusive_language">Abusive language</option>
                  <option value="spam">Spam / automated submissions</option>
                  <option value="repeated_duplicates">Repeated duplicate tickets</option>
                  <option value="harassment">Harassment</option>
                  <option value="chargeback_threat">Chargeback threat</option>
                  <option value="excessive_volume">Excessive volume</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, color: '#94a3b8' }}>Expires at (Optional):</label>
                <input
                  type="datetime-local"
                  className="ownerSupportSelect"
                  value={restrictionDraft.expiresAt}
                  onChange={(e) => setRestrictionDraft((d) => ({ ...d, expiresAt: e.target.value }))}
                />
              </div>
            </div>
            <div className="ownerModalActions">
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--default"
                onClick={() => setShowRestrictModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--primary"
                onClick={() => setShowRestrictConfirm(true)}
              >
                Apply Restrictions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Firm Confirm for Restrictions */}
      <OwnerFirmConfirm
        isOpen={showRestrictConfirm}
        title="Confirm Restriction"
        description={
          <span>
            Applying restrictions to <strong>{ticket.reporterEmail}</strong>.
            This action modifies the user profile and will be audited in <code>owner_action_log</code>.
          </span>
        }
        confirmLabel="Confirm Restrictions"
        busy={isSavingRestriction}
        onClose={() => setShowRestrictConfirm(false)}
        onConfirm={(reason) => handleSaveRestriction(reason)}
      />

      {/* Hard Confirm for Destructive Macros */}
      <OwnerHardConfirm
        isOpen={Boolean(macroToConfirm)}
        title={`Run Destructive Macro: ${macroToConfirm?.name || ''}`}
        targetHumanName={ticket.title || ticket.id}
        description={
          <span>
            Macro <strong>"{macroToConfirm?.name}"</strong> contains destructive steps.
            Running it on ticket <strong>"{ticket.title}"</strong> will execute mutating actions immediately.
            Type the ticket subject to confirm.
          </span>
        }
        busy={isRunningMacro}
        onClose={() => setMacroToConfirm(null)}
        onConfirm={() => {
          if (macroToConfirm) void executeMacro(macroToConfirm)
        }}
      />
    </div>
  )
}
